import { useState } from 'react';

import { TokenLaunchpadBadge } from '@/components/Badges/TokenLaunchpadBadge';
import { LiquidityScoreModal } from '@/components/Modal/LiquidityScoreModal';
import { SecurityModal } from '@/components/Modal/SecurityModuleModal';
import { LiquidityScore } from '@/components/TokenProfile/TokenLiquidityScore';
import { SecurityAuditPreview } from '@/components/TokenProfile/TokenProfileSecurityAudit/SecurityAuditPreview';

import { useSecurityAssessment } from '@/hooks/useSecurityAssessment';
import { useTokenInfo } from '@/hooks/useTokenInfo';

import type { TokenResult } from '@/utils/0x/token-registry.types';
import { isZoraCreatorCoin, isZoraToken, type ZoraType } from '@/utils/zora';

type TokenPanelStatsProps = {
  token: TokenResult;
};

/**
 * Qualitative token chips (launchpad, liquidity score, security audit) for the homepage
 * panel; the numeric stats live in `TokenPanelStatCells`. Unlike the token page there is
 * no section to scroll to, so the score badges open their detail modals instead.
 */
export const TokenPanelStats = ({ token }: TokenPanelStatsProps) => {
  const { data: tokenInfo } = useTokenInfo(token.address, token.chainId);
  const { level, risks, highRisks } = useSecurityAssessment({ activeToken: token });
  const [isSecurityOpen, setIsSecurityOpen] = useState(false);
  const [isLiquidityOpen, setIsLiquidityOpen] = useState(false);
  const [liquidityScore, setLiquidityScore] = useState<number>();

  const showSecurityAudit = level !== 'Pending' && risks + highRisks > 0;
  const launchpadName = token.launchpad?.name ?? tokenInfo?.launchpad;

  let zoraType: ZoraType;
  if (isZoraToken(token)) {
    zoraType = isZoraCreatorCoin(token) ? 'creator' : 'post';
  }

  const handleLiquidityClick = (score: number | undefined) => {
    setLiquidityScore(score);
    setIsLiquidityOpen(true);
  };

  return (
    <>
      {launchpadName && <TokenLaunchpadBadge id={launchpadName} />}
      <LiquidityScore activeToken={token} zoraType={zoraType} onClick={handleLiquidityClick} />
      {showSecurityAudit && (
        <SecurityAuditPreview activeToken={token} onClick={() => setIsSecurityOpen(true)} />
      )}
      <SecurityModal activeToken={token} isOpen={isSecurityOpen} onOpenChange={setIsSecurityOpen} />
      <LiquidityScoreModal
        activeToken={token}
        currentScore={liquidityScore}
        isOpen={isLiquidityOpen}
        onOpenChange={setIsLiquidityOpen}
      />
    </>
  );
};
